// API services for working with Google Business Profile through our backend

// In-memory caches and in-flight registries for ratings payloads.
const ratingSummaryCache = new Map();
const ratingDetailsCache = new Map();
const summaryRequestsInFlight = new Map();
const detailsRequestsInFlight = new Map();
const CACHE_EXPIRY = 15 * 60 * 1000; // 15 minutes
const SUMMARY_SESSION_STORAGE_KEY = 'rstc_google_rating_summary_cache_v1';

const DEFAULT_SUMMARY = Object.freeze({
  name: 'Error',
  formattedAddress: 'Could not load rating',
  rating: null,
  placeId: null,
  totalReviews: 0,
});

const DEFAULT_DETAILS = Object.freeze({
  ...DEFAULT_SUMMARY,
  formattedAddress: 'Could not load details',
  reviews: [],
});

const getCurrentTimestamp = () => Date.now();

const isCacheEntryFresh = (entry) => {
  if (!entry || typeof entry.timestamp !== 'number') return false;
  return (getCurrentTimestamp() - entry.timestamp) < CACHE_EXPIRY;
};

const safeLocationId = (locationId) => String(locationId || '').trim();

const hasWindowSessionStorage = () => {
  if (typeof window === 'undefined' || !window?.sessionStorage) return false;

  try {
    const probeKey = '__ratings_cache_probe__';
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
};

const readSummarySessionCache = () => {
  if (!hasWindowSessionStorage()) return {};

  try {
    const raw = window.sessionStorage.getItem(SUMMARY_SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeSummarySessionCache = (cacheObject) => {
  if (!hasWindowSessionStorage()) return;
  try {
    window.sessionStorage.setItem(SUMMARY_SESSION_STORAGE_KEY, JSON.stringify(cacheObject));
  } catch {
    // Ignore storage write errors (quota/private mode).
  }
};

const pruneExpiredSummarySessionEntries = (cacheObject) => {
  const pruned = {};
  Object.entries(cacheObject || {}).forEach(([locationId, entry]) => {
    if (isCacheEntryFresh(entry)) {
      pruned[locationId] = entry;
    }
  });
  return pruned;
};

const normalizeSummaryPayload = (data, locationId) => ({
  name: data?.name || 'Unknown',
  formattedAddress: data?.formattedAddress || locationId,
  rating: Number.isFinite(data?.rating) ? data.rating : null,
  placeId: data?.placeId || null,
  totalReviews: Number.isFinite(data?.totalReviews) ? data.totalReviews : 0,
});

const normalizeDetailsPayload = (data, locationId) => ({
  ...normalizeSummaryPayload(data, locationId),
  reviews: Array.isArray(data?.reviews) ? data.reviews : [],
});

const getFreshFromMemoryCache = (cache, locationId) => {
  const entry = cache.get(locationId);
  if (!entry) return null;

  if (!isCacheEntryFresh(entry)) {
    cache.delete(locationId);
    return null;
  }

  return entry.data;
};

const getFreshSummaryFromSessionCache = (locationId) => {
  const sessionCache = pruneExpiredSummarySessionEntries(readSummarySessionCache());
  const entry = sessionCache[locationId];
  if (!entry) {
    writeSummarySessionCache(sessionCache);
    return null;
  }

  writeSummarySessionCache(sessionCache);
  return entry.data || null;
};

const setSummaryCaches = (locationId, data) => {
  const timestamp = getCurrentTimestamp();
  ratingSummaryCache.set(locationId, { data, timestamp });

  const sessionCache = pruneExpiredSummarySessionEntries(readSummarySessionCache());
  sessionCache[locationId] = { data, timestamp };
  writeSummarySessionCache(sessionCache);
};

const setDetailsCache = (locationId, data) => {
  ratingDetailsCache.set(locationId, {
    data,
    timestamp: getCurrentTimestamp(),
  });
};

const fetchFromCandidates = async (candidateUrls) => {
  let lastError = null;

  for (const url of candidateUrls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastError = new Error(`Failed to fetch rating: ${response.status} ${response.statusText}`);
        continue;
      }
      return await response.json();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Failed to fetch rating payload');
};

export const fetchGoogleRatingSummary = async (locationId, options = {}) => {
  const normalizedLocationId = safeLocationId(locationId);
  if (!normalizedLocationId) {
    console.error('No locationId provided to fetchGoogleRatingSummary');
    return {
      ...DEFAULT_SUMMARY,
      formattedAddress: 'No locationId provided',
    };
  }

  const forceRefresh = Boolean(options?.forceRefresh);

  if (!forceRefresh) {
    const inMemory = getFreshFromMemoryCache(ratingSummaryCache, normalizedLocationId);
    if (inMemory) return inMemory;

    const sessionCached = getFreshSummaryFromSessionCache(normalizedLocationId);
    if (sessionCached) {
      ratingSummaryCache.set(normalizedLocationId, {
        data: sessionCached,
        timestamp: getCurrentTimestamp(),
      });
      return sessionCached;
    }
  }

  if (!forceRefresh && summaryRequestsInFlight.has(normalizedLocationId)) {
    return summaryRequestsInFlight.get(normalizedLocationId);
  }

  const requestPromise = (async () => {
    try {
      const payload = await fetchFromCandidates([
        `/api/ratings/${encodeURIComponent(normalizedLocationId)}/summary`,
        `/api/ratings/${encodeURIComponent(normalizedLocationId)}`,
      ]);
      const summary = normalizeSummaryPayload(payload, normalizedLocationId);
      setSummaryCaches(normalizedLocationId, summary);
      return summary;
    } catch (error) {
      console.error('Error fetching rating summary from database:', error);
      return {
        ...DEFAULT_SUMMARY,
        formattedAddress: normalizedLocationId,
      };
    }
  })();

  summaryRequestsInFlight.set(normalizedLocationId, requestPromise);
  try {
    return await requestPromise;
  } finally {
    if (summaryRequestsInFlight.get(normalizedLocationId) === requestPromise) {
      summaryRequestsInFlight.delete(normalizedLocationId);
    }
  }
};

/**
 * Fetch reviews and rating for a specific location from our database.
 * @param {string} locationId - Location ID in our database.
 * @returns {Promise<{reviews: Object[], rating: number, name?: string, formattedAddress?: string, placeId?: string } | null>} - Object with reviews and rating or null in case of error.
 */
export const fetchGooglePlaceDetails = async (locationId, options = {}) => {
  const normalizedLocationId = safeLocationId(locationId);
  if (!normalizedLocationId) {
    console.error('No locationId provided to fetchGooglePlaceDetails');
    return {
      ...DEFAULT_DETAILS,
      formattedAddress: 'No locationId provided',
    };
  }

  const forceRefresh = Boolean(options?.forceRefresh);
  if (!forceRefresh) {
    const inMemory = getFreshFromMemoryCache(ratingDetailsCache, normalizedLocationId);
    if (inMemory) return inMemory;
  }

  if (!forceRefresh && detailsRequestsInFlight.has(normalizedLocationId)) {
    return detailsRequestsInFlight.get(normalizedLocationId);
  }

  const requestPromise = (async () => {
    try {
      const payload = await fetchFromCandidates([
        `/api/ratings/${encodeURIComponent(normalizedLocationId)}/details`,
        `/api/ratings/${encodeURIComponent(normalizedLocationId)}`,
      ]);
      const details = normalizeDetailsPayload(payload, normalizedLocationId);
      setDetailsCache(normalizedLocationId, details);
      setSummaryCaches(normalizedLocationId, normalizeSummaryPayload(details, normalizedLocationId));
      return details;
    } catch (error) {
      console.error('Error fetching rating from database:', error);
      return {
        ...DEFAULT_DETAILS,
        formattedAddress: normalizedLocationId,
      };
    }
  })();

  detailsRequestsInFlight.set(normalizedLocationId, requestPromise);
  try {
    return await requestPromise;
  } finally {
    if (detailsRequestsInFlight.get(normalizedLocationId) === requestPromise) {
      detailsRequestsInFlight.delete(normalizedLocationId);
    }
  }
};

/**
 * Fetch all ratings from our database
 * @returns {Promise<Array>} - Array with ratings for all locations
 */
export const fetchAllRatings = async () => {
  try {
    const response = await fetch('/api/ratings');
    if (!response.ok) {
      throw new Error(`Failed to fetch all ratings: ${response.status} ${response.statusText}`);
    }

    const payload = await response.json();
    const ratings = Array.isArray(payload) ? payload : (Array.isArray(payload?.ratings) ? payload.ratings : []);

    // Update summary cache for each location.
    ratings.forEach((rating) => {
      if (rating.locationId) {
        const normalizedLocationId = safeLocationId(rating.locationId);
        const summary = normalizeSummaryPayload(rating, normalizedLocationId);
        setSummaryCaches(normalizedLocationId, summary);
      }
    });

    return ratings;
  } catch (error) {
    console.error('Error fetching all ratings:', error);
    return [];
  }
};

/**
 * Clear ratings cache
 */
export const clearRatingsCache = () => {
  ratingSummaryCache.clear();
  ratingDetailsCache.clear();
  summaryRequestsInFlight.clear();
  detailsRequestsInFlight.clear();
  if (hasWindowSessionStorage()) {
    try {
      window.sessionStorage.removeItem(SUMMARY_SESSION_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};

/**
 * @deprecated Use fetchGooglePlaceDetails instead
 */
export const fetchGoogleReviews = async (locationId) => {
  console.warn('fetchGoogleReviews is deprecated. Use fetchGooglePlaceDetails instead.');
  const data = await fetchGooglePlaceDetails(locationId);
  return data.reviews || [];
};

/**
 * @deprecated Use fetchGooglePlaceDetails to get all data at once.
 * Get average rating for a location.
 */
export const fetchAverageRating = async (locationId) => {
  console.warn('fetchAverageRating is deprecated. Use fetchGooglePlaceDetails instead.');
  const data = await fetchGooglePlaceDetails(locationId);
  return data.rating || 4.2; // Return default value if rating is null
};
